"use client";

import { createContext, useContext } from "react";
import { GripVertical } from "lucide-react";
import {
    Group,
    Panel,
    Separator,
    type GroupProps,
    type PanelProps,
    type SeparatorProps,
} from "react-resizable-panels";

import { cn } from "@/lib/utils";

type Direction = "horizontal" | "vertical";

const DirectionContext = createContext<Direction>("horizontal");

type ResizablePanelGroupProps = Omit<GroupProps, "orientation"> & {
    direction?: Direction;
};

const ResizablePanelGroup = ({
    className,
    direction = "horizontal",
    ...props
}: ResizablePanelGroupProps) => (
    <DirectionContext.Provider value={direction}>
        <Group
            orientation={direction}
            className={cn(
                "flex h-full w-full",
                direction === "vertical" && "flex-col",
                className
            )}
            {...props}
        />
    </DirectionContext.Provider>
);

const ResizablePanel = (props: PanelProps) => <Panel {...props} />;

type ResizableHandleProps = SeparatorProps & {
    withHandle?: boolean;
    /**
     * When true, render the grip absolutely centered on the separator line
     * (half above / half below) instead of inline inside a flex-centered box.
     * Default: false — keeps the original shadcn look.
     */
    floatingHandle?: boolean;
};

const ResizableHandle = ({
    withHandle,
    floatingHandle = false,
    className,
    ...props
}: ResizableHandleProps) => {
    const direction = useContext(DirectionContext);
    const isVertical = direction === "vertical";

    return (
        <Separator
            className={cn(
                "relative bg-border outline-none focus:outline-none focus-visible:outline-none [&_*]:outline-none",
                isVertical
                    ? "h-px w-full after:absolute after:inset-x-0 after:top-1/2 after:h-3 after:-translate-y-1/2"
                    : "w-px h-full after:absolute after:inset-y-0 after:left-1/2 after:w-3 after:-translate-x-1/2",
                !floatingHandle && "flex items-center justify-center",
                className
            )}
            {...props}
        >
            {withHandle && (
                <div
                    className={cn(
                        "z-10 flex items-center justify-center rounded-sm border bg-border",
                        isVertical ? "h-3 w-4" : "h-4 w-3",
                        floatingHandle &&
                            "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                    )}
                >
                    <GripVertical
                        className={cn(
                            "h-2.5 w-2.5",
                            isVertical && "rotate-90"
                        )}
                    />
                </div>
            )}
        </Separator>
    );
};

export { ResizablePanelGroup, ResizablePanel, ResizableHandle };
